import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  color: #333;
  margin: 0 0 16px 0;
  font-size: 24px;
`;

const Description = styled.p`
  color: #666;
  margin: 0 0 24px 0;
  font-size: 16px;
  line-height: 1.5;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 20px;
  box-sizing: border-box;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.primary {
    background: #007AFF;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background: #f8f9fa;
    color: #666;
    border: 1px solid #ddd;
    
    &:hover {
      background: #e9ecef;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background: #f8d7da;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const AppIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

// Password configuration - moved to environment variables for security
const APP_PASSWORDS = {
  dreamhotties: process.env.REACT_APP_DREAMHOTTIES_PASSWORD || 'dream2024',
  oracle: process.env.REACT_APP_ORACLE_PASSWORD || 'health2024'
};

const APP_CONFIG = {
  dreamhotties: {
    name: 'DreamHotties',
    icon: '🔥',
    description: 'Advanced AI image generation platform with premium features.'
  },
  oracle: {
    name: 'Health Oracle', 
    icon: '🏥',
    description: 'Comprehensive health analysis and medical consultation tools.'
  }
};

const PasswordProtection = ({ appKey, onSuccess, onCancel }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const appConfig = APP_CONFIG[appKey];
  const correctPassword = APP_PASSWORDS[appKey];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    setIsLoading(true);
    setError('');

    // Simulate a small delay for security
    await new Promise(resolve => setTimeout(resolve, 500));

    if (password === correctPassword) {
      // Store access in sessionStorage (expires when browser closes)
      sessionStorage.setItem(`${appKey}_access`, 'granted');
      onSuccess();
    } else {
      setError('Incorrect password. Please contact support for access.');
      setPassword('');
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <Overlay>
      <Modal>
        <AppIcon>{appConfig.icon}</AppIcon>
        <Title>Access {appConfig.name}</Title>
        <Description>{appConfig.description}</Description>
        <Description style={{ fontSize: '14px', color: '#888' }}>
          This app requires a special access code. Contact your administrator for credentials.
        </Description>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <form onSubmit={handleSubmit}>
          <Input
            type="password"
            placeholder="Enter access code"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            autoFocus
          />
          
          <ButtonContainer>
            <Button 
              type="button" 
              className="secondary" 
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="primary"
              disabled={isLoading || !password.trim()}
            >
              {isLoading ? 'Verifying...' : 'Access App'}
            </Button>
          </ButtonContainer>
        </form>
        
        <div style={{ 
          fontSize: '12px', 
          color: '#999', 
          marginTop: '20px',
          borderTop: '1px solid #eee',
          paddingTop: '16px'
        }}>
          💡 Access codes are case-sensitive<br/>
          🔒 Your session will expire when you close the browser
        </div>
      </Modal>
    </Overlay>
  );
};

// Helper function to check if app access is already granted
export const hasAppAccess = (appKey) => {
  return sessionStorage.getItem(`${appKey}_access`) === 'granted';
};

// Helper function to revoke app access
export const revokeAppAccess = (appKey) => {
  sessionStorage.removeItem(`${appKey}_access`);
};

export default PasswordProtection;